import {compact} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  NodeDetailsSpec,
  ProcessFlowchartConfigSpec,
  ProcessFlowchartEdgeConfigSpec,
  ProcessFlowchartNodeConfigSpec,
  ProcessFlowchartSchemaMapperSpec,
  ProcessFlowchartSpec,
  TaskDefinitionVersionSpec,
} from 'processes/dao/ProcessFlowchartConfigDAO';
import {convertToRouterConfigSpec} from 'processes/modals/router-config/RouterConfigUtils';
import {
  StaticTemplateFlowchart,
  ProcessFlowchartEdgeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import buildSchemaMapperConfigsFromStaticData from './buildSchemaMapperConfigFromStaticData';
import {convertFrontendTaskDefinitionVersionSpecToNetworkSpec} from './processFlowchartConfigEditorNetworkUtils';
import {
  isStaticEndNodeData,
  isStaticRouterNodeData,
  isStaticTaskSchemaNodeData,
  RouterStaticNodeData,
  EndStaticNodeData,
  TaskSchemaStaticNodeData,
  isStaticPlaceholderNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigMetadata} from './redux/reduceFlowchartConfig';

export interface BuildNetworkFlowchartFromStaticDataRunnerOptions extends Omit<
  FlowchartConfigMetadata,
  'interactionState' | 'currentVersionId' | 'didInitialFlowchartHaveChainedRouters' | 'flowchartTypeName'
> {
  flowchartConfigId: string;
}

interface BuildNetworkFlowchartFromStaticDataRunnerArgs {
  staticFlowchart: StaticTemplateFlowchart;
  options: BuildNetworkFlowchartFromStaticDataRunnerOptions;
}

class BuildNetworkFlowchartFromStaticDataRunner {
  private readonly staticFlowchart: StaticTemplateFlowchart;
  private readonly options: BuildNetworkFlowchartFromStaticDataRunnerOptions;
  private inboundSchemaMapperConfigsByNodeId!: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;
  private rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;

  constructor(args: BuildNetworkFlowchartFromStaticDataRunnerArgs) {
    this.staticFlowchart = args.staticFlowchart;
    this.options = args.options;

    assert(this.options.rootTaskSchema != null, 'Expected root task schema to be defined');
    this.rootTaskSchema = this.options.rootTaskSchema;
  }

  run(): ProcessFlowchartConfigSpec {
    return this.convertStaticFlowchartToNetworkFlowchartConfigSpec();
  }

  /**
   * Converts a static flowchart into a network spec ProcessFlowchartConfigSpec used for updates.
   */
  private convertStaticFlowchartToNetworkFlowchartConfigSpec(): ProcessFlowchartConfigSpec {
    const {
      // TODO: This is just for schema mapper configs in the flowchart, doesn't account for those outside of the flowchart.
      // To be solved once ACM/PCM decision made.
      schemaMapperConfigSpecs: updatedSchemaMapperConfigSpecs,
      // Inbound schema mappers are only those that are used in the flowchart.
      inboundSchemaMapperConfigsByNodeId,
    } = buildSchemaMapperConfigsFromStaticData({
      nodes: this.staticFlowchart.nodesStaticData,
      edges: this.staticFlowchart.edgesStaticData,
      customMappingDataByEdgeId: this.options.customMappingDataByEdgeId,
      transitionSourceByNodeIdPair: this.options.transitionSourceByNodeIdPair,
    });

    this.inboundSchemaMapperConfigsByNodeId = inboundSchemaMapperConfigsByNodeId;

    const {rootTaskDefinitionVersionSpec, allTaskDefinitionVersionSpecs} =
      this.extractTaskDefinitionVersionSpecs();

    const availableTaskSchemaIds = Array.from(
      new Set(
        compact(
          this.staticFlowchart.nodesStaticData
            .filter(
              (node) =>
                isStaticTaskSchemaNodeData(node) &&
                node.nodeDetailsType !== ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS &&
                node.nodeDetailsType !== ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
            )
            .map((node: TaskSchemaStaticNodeData) => node.taskSchemaId)
        )
      )
    );

    return {
      rootProcessTaskDefinitionVersion: rootTaskDefinitionVersionSpec,
      allProcessTaskDefinitionVersions: allTaskDefinitionVersionSpecs,
      templateFlowchart: this.getTemplateFlowchartSpec(),
      isFlowchartModifiableOnCreation: this.options.options.isFlowchartModifiableOnCreation,
      isFlowchartModifiableAfterCreation: this.options.options.isFlowchartModifiableAfterCreation,
      availableTaskSchemaIds,
      schemaMapperConfigs: updatedSchemaMapperConfigSpecs,
    };
  }

  private extractTaskDefinitionVersionSpecs(): {
    rootTaskDefinitionVersionSpec: TaskDefinitionVersionSpec;
    allTaskDefinitionVersionSpecs: Array<TaskDefinitionVersionSpec>;
  } {
    const rootNode = this.staticFlowchart.nodesStaticData.find(
      (node) =>
        isStaticTaskSchemaNodeData(node) &&
        node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    ) as TaskSchemaStaticNodeData | undefined;
    assert(rootNode != null, 'Expected root node to be defined', {
      nodes: this.staticFlowchart.nodesStaticData,
    });
    const rootTaskDefinitionVersionSpec = convertFrontendTaskDefinitionVersionSpecToNetworkSpec(
      rootNode.taskDefinitionVersionSpec,
      this.options.flowchartConfigId
    );

    // Note, this clobbers duplicate task definition version specs. If we have two nodes using the same
    // task definition version and the same client ID, we will only keep the object for the last node.
    // This may become an issue in the future when making edits to process task definition versions.
    const allTaskDefinitionVersionSpecs = Array.from(
      new Map(
        this.staticFlowchart.nodesStaticData
          .filter((node) => isStaticTaskSchemaNodeData(node))
          .map((node) => {
            const spec = convertFrontendTaskDefinitionVersionSpecToNetworkSpec(
              node.taskDefinitionVersionSpec,
              this.options.flowchartConfigId
            );
            return [spec.clientId, spec] as const;
          })
      ).values()
    );
    return {rootTaskDefinitionVersionSpec, allTaskDefinitionVersionSpecs};
  }

  private getNodeDetailsFromStaticNode(
    node: TaskSchemaStaticNodeData | RouterStaticNodeData | EndStaticNodeData
  ): NodeDetailsSpec {
    switch (node.nodeDetailsType) {
      case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS: {
        assert(isStaticTaskSchemaNodeData(node), 'Expected root node to be a task schema node');
        return {
          type: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
          taskSchemaId: this.rootTaskSchema.id,
          taskDefinitionVersionClientId: node.taskDefinitionVersionSpec.clientId,
        };
      }
      case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS: {
        assert(isStaticTaskSchemaNodeData(node), 'Expected output node to be a task schema node');
        assert(
          this.rootTaskSchema.outputSchema != null,
          'Expected flowchart with output node to have output schema',
          {node, flowchartTaskSchema: this.rootTaskSchema}
        );
        return {
          type: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
          outputSchemaId: this.rootTaskSchema.outputSchema.id,
        };
      }
      case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS: {
        assert(isStaticTaskSchemaNodeData(node), 'Expected task node to be a task schema node');
        return {
          type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
          taskSchemaId: node.taskSchemaId,
          taskDefinitionVersionClientId: node.taskDefinitionVersionSpec.clientId,
          extension: null,
        };
      }
      case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS: {
        assert(isStaticRouterNodeData(node), 'Expected router node to be a router node');
        const routerFunctionSpecs = [
          ...node.routerConfig.functionSpecs,
          node.routerConfig.defaultFunctionSpec,
        ].map((functionSpec) => {
          assert(functionSpec != null, 'Expected router function spec to be defined');
          return convertToRouterConfigSpec(functionSpec, /* flowchartSpecHasEdgeToOutput */ true);
        });
        return {
          type: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
          name: node.routerConfig.name,
          routerFunctions: routerFunctionSpecs,
        };
      }
      case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS: {
        assert(isStaticEndNodeData(node), 'Expected end node to be an end node');
        return {
          type: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
          name: node.nodeName,
        };
      }
      default:
        assert(false, `Unexpected node ${node.nodeDetailsType}}`);
    }
  }

  private getTemplateFlowchartSpec(): ProcessFlowchartSpec {
    const nodeSpecs: Array<ProcessFlowchartNodeConfigSpec> = [];
    for (const node of this.staticFlowchart.nodesStaticData) {
      // Placeholder nodes do not have additional node details so we skip them here.
      if (isStaticPlaceholderNodeData(node)) {
        continue;
      }
      if (this.inboundSchemaMapperConfigsByNodeId[node.id] != null) {
        const inboundSchemaMappers = this.inboundSchemaMapperConfigsByNodeId[node.id];
        const nodeDetails = this.getNodeDetailsFromStaticNode(node);

        const mappableNodeSpec = {
          clientId: node.id,
          nodeDetails,
          inboundSchemaMappers,
        };

        nodeSpecs.push(mappableNodeSpec);
      } else {
        const nodeDetails = this.getNodeDetailsFromStaticNode(node);
        nodeSpecs.push({
          clientId: node.id,
          nodeDetails,
        });
      }
    }

    const edgeSpecs: Array<ProcessFlowchartEdgeConfigSpec> = this.staticFlowchart.edgesStaticData
      .filter((edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW)
      .map((edge) => ({
        clientId: edge.id,
        fromNodeClientId: edge.source,
        toNodeClientId: edge.target,
      }));

    return {
      clientId: this.options.flowchartConfigId,
      nodes: nodeSpecs,
      edges: edgeSpecs,
    };
  }
}

export default function buildNetworkFlowchartFromStaticData(
  args: BuildNetworkFlowchartFromStaticDataRunnerArgs
): ProcessFlowchartConfigSpec {
  const runner = new BuildNetworkFlowchartFromStaticDataRunner(args);
  return runner.run();
}
